import {Box, IconButton, InputAdornment, Popover} from '@mui/material';
import {GridColDef, GridFilterItem, GridFilterModel} from '@mui/x-data-grid';
import {GridApi} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isEmpty} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {ReactComponent as ClearIcon} from '../../../assests/svg/clear-Icon-filled.svg';

export interface FieldFilterGridData {
  id: number | string;
  label: string;
  value?: string;
}

export interface FieldPopoverGridFilterProps {
  apiRef: React.MutableRefObject<GridApi>;
  dataArray?: FieldFilterGridData[];
  defaultOptions?: string[];
  valueGetterObj?: object;
  columnField: string;
  hideFooter?: boolean;
  inputClass?: string;
  inputType?: 'text' | 'search';
  caretIconContainerClass?: string;
  dataTestId?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  useDefaultLabel?: boolean;
  label?: string;
  useDefaultPlaceholder?: boolean;
  placeholder?: string;
  selectionType?: 'single' | 'multiple';
}

const FilterBooleanListField: React.FC<FieldPopoverGridFilterProps> = (
  props: FieldPopoverGridFilterProps
) => {
  const {
    apiRef,
    columnField,
    dataArray = [],
    defaultOptions = [],
    valueGetterObj,
    hideFooter = false,
    inputClass,
    inputType = 'search',
    caretIconContainerClass,
    dataTestId,
    variant = 'filled',
    useDefaultLabel = false,
    label,
    useDefaultPlaceholder = true,
    placeholder,
    selectionType = 'multiple'
  } = props;

  const iconButtonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIdArray, setSelectedIdArray] = useState<(string | number)[]>([]);

  const filterApplied = useRef<boolean>(false);

  const filterColumns: GridColDef<FieldFilterGridData[][number]>[] = useMemo(
    () => [
      {
        field: 'label',
        headerName: t('common.selectAll'),
        width: 220,
        sortable: false,
        resizable: false,
        filterable: false,
        valueGetter: (value) => {
          return !isEmpty(valueGetterObj)
            ? (valueGetterObj?.[value] ?? '')
            : value;
        },
      },
    ],
    [valueGetterObj]
  );

  useEffect(() => {
    const selectedIds = [];
    for (const option of defaultOptions) {
        const item = dataArray.find(i => i.value === option);
        if (!item) { continue; }
        const index = dataArray.indexOf(item);
        selectedIds.push(index + 1);
    }

    if (selectedIds.length > 0) { filterApplied.current = true; }

    setSelectedIdArray(selectedIds);
  }, []);

  const handleOpen = () => {
    setAnchorEl(iconButtonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = useCallback((overrideIds?: (string | number)[]) => {
    const filterModel: GridFilterModel = apiRef.current.state.filter.filterModel;
    const originalFilters = filterModel.items.filter(item => !dataArray.some(i => i.value === item.field))

    const idArray = overrideIds ?? selectedIdArray;

    const selectedFields: string[] = dataArray
    .filter(i => idArray.includes(i.id))
    .map(i => i.value ? i.value : i.label);

    let id = 0;
    const newFilters: GridFilterItem[] = [];
    for (const field of selectedFields) {
        newFilters.push({
            id: id++,
            field: field,
            operator: 'eq',
            value: true,
        });
    }

    const updatedFilterModel: GridFilterModel = {
        items: [
            ...newFilters,
            ...originalFilters,
        ]
    };

    apiRef.current?.setFilterModel(updatedFilterModel, 'upsertFilterItems');
    filterApplied.current = true;
    setAnchorEl(null);
  }, [apiRef, dataArray, selectedIdArray, columnField, handleClose]);

  const handleClearAll = useCallback(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    const updatedFilterModel: GridFilterModel = {
      items: [...filterModel.items]
    };

    for (const option of dataArray) {
        const item = updatedFilterModel.items.find(i => i.field === option.value);
        if (item) {
            const index = updatedFilterModel.items.indexOf(item);
            updatedFilterModel.items.splice(index, 1);
        }
    }

    apiRef.current.setFilterModel(updatedFilterModel, 'upsertFilterItems');

    setSelectedIdArray([]);
    setAnchorEl(null);
  }, [apiRef, columnField]);

  const handleSelectionChange = (selectedIds: (string | number)[]) => {
    setSelectedIdArray(selectedIds);
    if (selectionType === 'single') { handleApply(selectedIds); }
  };

  const inputValue = useMemo(() => {
    if (!isEmpty(selectedIdArray ?? []) && filterApplied.current) {
      const count = selectedIdArray.length;
      return t('fieldManagement.fieldTable.filter') + ' (' + count + ')';
    }

    return '';
  }, [selectedIdArray]);

  const handleKeyPress = (
    event: React.KeyboardEvent<Element>,
    keys: string[],
    action: () => void
) => {
    const key = event.key;
    const code = event.code;
    if (keys.some(i => i === key) || keys.some(i => i === code)) {
        action?.();
    }
  };

  return (
    <>
      <Box className={'aur-field-popover-grid-filter'} ref={iconButtonRef}>
        <AurTextField
          className={`aur-search-input ${inputClass ?? ''}`}
          autoComplete={'off'}
          type={inputType}
          onClick={handleOpen}
          onKeyDown={(e) => handleKeyPress(e, ['Enter'], handleOpen)}
          variant={variant}
          size={'small'}
          placeholder={useDefaultPlaceholder ? t('fieldManagement.fieldTable.filter') : placeholder}
          label={useDefaultLabel ? t('fieldManagement.fieldTable.filter') : label}
          value={inputValue}
          onChange={handleOpen}
          ariaLabel={useDefaultLabel ? t('fieldManagement.fieldTable.filter') : label}
          InputProps={{
            endAdornment: !isEmpty(selectedIdArray ?? []) &&
              inputType === 'text' && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleClearAll();
                    }}
                    edge="end"
                    tabIndex={0}
                  >
                    <ClearIcon aria-hidden="true" />
                  </IconButton>
                </InputAdornment>
              ),
          }}
          data-testid={dataTestId}
        />
        <Box
          className={`aur-caret-icon-container ${caretIconContainerClass ?? ''}`}
        >
          <IconButton
            aria-label={`dropdown-${columnField}`}
            data-testid={`dropdown-${columnField}`}
            className={'aur-caret-icon-button'}
            onClick={handleOpen}
            tabIndex={0}
          >
            <AurIcon
              icon={anchorEl ? IconNames.CaretUp : IconNames.CaretDown}
              aria-hidden="true"
            />
          </IconButton>
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        disableScrollLock={false}
        className="filter-modal-popover"
      >
        <Box
          role="region"
          aria-label={t('fieldManagement.fieldTable.filterGrid')}
        >
          <AurDataGrid
            key={'filter-table'}
            rows={dataArray}
            columns={filterColumns}
            showPagination={false}
            selectedIds={selectedIdArray}
            currentPage={0}
            onSelectionChange={handleSelectionChange}
            itemsPerPage={dataArray.length}
            checkboxSelection={selectionType === 'single' ? false : true}
            columnHeaderHeight={selectionType === 'single' ? 0 : undefined}
            hideFooter={hideFooter || selectionType === 'single' ? true : false}
            footerChildComponent={
              <Box className={'aur-filter-button-container'}>
                <AurButton
                  onClick={handleClearAll}
                  onKeyDown={(e) => handleKeyPress(e, ['Enter', ' '], handleClearAll)}
                  size={'Small'}
                  label={t('common.clearAll')}
                  type={'text'}
                  ariaLabel={t('common.clearAll')}
                  dataTestId="clear-grid-filter-button"
                />
                <AurButton
                  onClick={() => handleApply()}
                  onKeyDown={(e) => handleKeyPress(e, ['Enter', ' '], handleApply)}
                  size={'Small'}
                  label={t('buttons.apply')}
                  type={'continue'}
                  ariaLabel={t('buttons.apply')}
                  dataTestId="apply-grid-filter-button"
                />
              </Box>
            }
          />
        </Box>
      </Popover>
    </>
  );
};

export default FilterBooleanListField;
